import React, { useState } from "react";
import { Button, Box, Typography, CircularProgress, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import CommonModal from "../common/CommonModal";
import { useHouse } from "../../hooks/useHouse";

interface StudentDataSheetSortModalProps {
  open: boolean;
  onClose: () => void;
  onSelectSortBy: (sortBy: "rollnumber" | "studentname" | "house", houseName?: string) => void;
  isLoading?: boolean;
}

const OPTION_BTN_SX = {
  py: 2,
  fontSize: "1rem",
  fontWeight: "medium",
  borderColor: "#3092e3",
  color: "#3092e3",
  "&:hover": { borderColor: "#1976d2", backgroundColor: "#e3f2fd" },
};

const StudentDataSheetSortModal: React.FC<StudentDataSheetSortModalProps> = ({
  open,
  onClose,
  onSelectSortBy,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [showHouses, setShowHouses] = useState(false);
  const { getAllHouseQuery } = useHouse();

  const handleClose = () => {
    setShowHouses(false);
    onClose();
  };

  const modalTitle = showHouses ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <IconButton
        onClick={() => setShowHouses(false)}
        size="small"
        disabled={isLoading}
        sx={{ color: "#fff", p: 0.25, "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" } }}
      >
        <ArrowBackIcon fontSize="small" />
      </IconButton>
      {t("select_house") || "Select House"}
    </Box>
  ) : (t("sort_by") || "Sort By");

  return (
    <CommonModal
      open={open}
      onClose={() => { if (!isLoading) handleClose(); }}
      title={modalTitle}
      maxWidth="sm"
      disableEscapeKeyDown={isLoading}
      customActions={
        <>
          {showHouses && (
            <Button
              onClick={() => setShowHouses(false)}
              color="primary"
              disabled={isLoading}
              sx={{ mr: "auto" }}
            >
              {t("back") || "Back"}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              borderColor: "#3092e3",
              color: "#3092e3",
              "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" },
            }}
          >
            {t("cancel") || "Cancel"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {!showHouses ? (
          <>
            <Button variant="outlined" fullWidth size="large" onClick={() => onSelectSortBy("rollnumber")} disabled={isLoading} sx={OPTION_BTN_SX}>
              {t("roll_number") || "Roll Number"}
            </Button>

            <Button variant="outlined" fullWidth size="large" onClick={() => onSelectSortBy("studentname")} disabled={isLoading} sx={OPTION_BTN_SX}>
              {t("student_name") || "Student Name"}
            </Button>

            <Button variant="outlined" fullWidth size="large" onClick={() => setShowHouses(true)} disabled={isLoading} sx={OPTION_BTN_SX}>
              {t("houses") || "Houses"}
            </Button>
          </>
        ) : getAllHouseQuery.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !getAllHouseQuery.data || getAllHouseQuery.data.length === 0 ? (
          <Typography variant="body1" align="center" color="text.secondary">
            {t("no_houses_found") || "No houses found."}
          </Typography>
        ) : (
          getAllHouseQuery.data.map((house) => (
            <Button
              key={house.id}
              variant="outlined"
              fullWidth
              size="large"
              onClick={() => onSelectSortBy("house", house.houseName)}
              disabled={isLoading}
              sx={{
                ...OPTION_BTN_SX,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: house.houseColor || "#ccc",
                  border: "1px solid rgba(0,0,0,0.1)",
                }}
              />
              {house.houseName}
            </Button>
          ))
        )}
      </Box>
    </CommonModal>
  );
};

export default StudentDataSheetSortModal;
